import type { DeviceSpecDevice, SpecMachine } from '../../types'
import type { FormulaField } from './recompute'

// 명세서 검색 — 태그 칸(Alias·주소·설명·연산·크기, 수식 원문 포함)·디바이스 칸, 마스터데이터(인터페이스 칸·항목 전송코드·설명·태그 주소)에서 찾는다 (대소문자 무시)

export type TagSearchField = 'alias' | 'address' | 'description' | 'arithmetic' | 'size'

export interface SearchHit {
  deviceKey: string
  deviceLabel: string
  row: number | null // 태그 행 (디바이스 칸에서 찾았으면 null)
  field: TagSearchField | null
  deviceField: DeviceSearchField | null // 디바이스 칸에서 찾았을 때 그 칸
  label: string // 칸 이름
  value: string // 보여 줄 글자 (수식이면 "값  ← 수식")
  // 마스터데이터에서 찾았을 때 — 인터페이스 키, 항목 행(인터페이스 칸이면 null)과 칸. 이때 deviceKey 는 ''
  machine?: { key: string; row: number | null; field: MachineSearchField | ItemSearchField }
}

export type MachineSearchField = 'code' | 'name' | 'description'
export type ItemSearchField = 'code' | 'description' | 'address'
const MACHINE_FIELDS: { key: MachineSearchField; label: string }[] = [
  { key: 'code', label: '인터페이스 ID' }, { key: 'name', label: '인터페이스 이름' }, { key: 'description', label: '인터페이스 설명' },
]
const ITEM_FIELDS: { key: ItemSearchField; label: string }[] = [
  { key: 'code', label: '전송코드' }, { key: 'description', label: '항목 설명' }, { key: 'address', label: '항목 태그 주소' },
]

const TAG_FIELDS: { field: TagSearchField; label: string }[] = [
  { field: 'alias', label: 'Alias' },
  { field: 'address', label: '주소' },
  { field: 'description', label: '설명' },
  { field: 'arithmetic', label: '연산' },
  { field: 'size', label: '크기' },
]

export type DeviceSearchField = 'name' | 'code' | 'category' | 'maker' | 'model' | 'memo' | 'description'

const DEVICE_FIELDS: { key: DeviceSearchField; label: string }[] = [
  { key: 'name', label: '디바이스 이름' },
  { key: 'code', label: '디바이스 코드' },
  { key: 'category', label: '설비구분' },
  { key: 'maker', label: '제조사' },
  { key: 'model', label: '모델' },
  { key: 'memo', label: '메모' },
  { key: 'description', label: '디바이스 설명' },
]

export function searchSpec(devices: DeviceSpecDevice[], query: string, limit = 100, machines: SpecMachine[] = []): { hits: SearchHit[]; total: number } {
  const q = query.trim().toLowerCase()
  if (!q) return { hits: [], total: 0 }
  const hits: SearchHit[] = []
  let total = 0
  const push = (hit: SearchHit) => { total++; if (hits.length < limit) hits.push(hit) }

  devices.forEach((d, di) => {
    const deviceLabel = d.name || d.code || `디바이스 ${di + 1}`
    for (const { key, label } of DEVICE_FIELDS) {
      const v = d[key] ?? ''
      if (v.toLowerCase().includes(q)) push({ deviceKey: d.key, deviceLabel, row: null, field: null, deviceField: key, label, value: v })
    }
    d.tags.forEach((t, row) => {
      for (const { field, label } of TAG_FIELDS) {
        const value = t[field] ?? ''
        const formula = field !== 'arithmetic' ? t.formulas?.[field as FormulaField] : undefined
        if (value.toLowerCase().includes(q) || formula?.toLowerCase().includes(q)) {
          push({ deviceKey: d.key, deviceLabel, row, field, deviceField: null, label, value: formula ? `${value}  ← ${formula}` : value })
        }
      }
    })
  })

  machines.forEach((m, mi) => {
    const deviceLabel = m.code || m.name || `인터페이스 ${mi + 1}`
    for (const { key, label } of MACHINE_FIELDS) {
      const v = m[key] ?? ''
      if (v.toLowerCase().includes(q)) push({ deviceKey: '', deviceLabel, row: null, field: null, deviceField: null, label, value: v, machine: { key: m.key, row: null, field: key } })
    }
    m.items.forEach((item, row) => {
      for (const { key, label } of ITEM_FIELDS) {
        const v = item[key] ?? ''
        if (v.toLowerCase().includes(q)) push({ deviceKey: '', deviceLabel, row, field: null, deviceField: null, label, value: v, machine: { key: m.key, row, field: key } })
      }
    })
  })
  return { hits, total }
}
